import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import DealsTable from './components/DealsTable';
import DealsFilters from './components/DealsFilters';
import DealDrawer from './components/DealDrawer';
import TablePagination from './components/TablePagination';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const DealsPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [deals, setDeals] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stages, setStages] = useState([]);
  const [filters, setFilters] = useState({ search: '', stage: '', owner: '', minValue: '', maxValue: '', closeDateFrom: '', closeDateTo: '' });
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newDeal, setNewDeal] = useState({ name: '', value: '', stage_id: '', close_date: '', probability: 50, priority: 'medium' });
  const [saving, setSaving] = useState(false);
  const { profile } = useAuth();

  const handleClearFilters = () => {
    setFilters({ search: '', stage: '', owner: '', minValue: '', maxValue: '', closeDateFrom: '', closeDateTo: '' });
  };

  const handleBulkAction = (action) => {
    // Placeholder for bulk actions
    console.log('Bulk action:', action, 'for deals:', selectedDeals);
  };

  const fetchStages = useCallback(async () => {
    if (!profile?.org_id) return;
    const { data } = await supabase?.from('pipeline_stages')?.select('*')?.eq('org_id', profile?.org_id)?.order('stage_order');
    if (data) setStages(data);
  }, [profile?.org_id]);

  const fetchDeals = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    setError('');
    try {
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;

      let query = supabase?.from('deals')?.select('*, accounts(name), contacts(first_name, last_name), pipeline_stages(name, color), owner:profiles(full_name)', { count: 'exact' })?.eq('org_id', profile?.org_id)?.is('deleted_at', null)?.order(sortConfig?.key, { ascending: sortConfig?.direction === 'asc' })?.range(from, to);

      if (filters?.search) query = query?.ilike('name', `%${filters?.search}%`);
      if (filters?.stage) query = query?.eq('stage_id', filters?.stage);
      if (filters?.minValue) query = query?.gte('value', Number(filters?.minValue));
      if (filters?.maxValue) query = query?.lte('value', Number(filters?.maxValue));
      if (filters?.closeDateFrom) query = query?.gte('close_date', filters?.closeDateFrom);
      if (filters?.closeDateTo) query = query?.lte('close_date', filters?.closeDateTo);

      const { data, error: fetchError, count } = await query;
      if (fetchError) throw fetchError;

      const mapped = (data || [])?.map(d => ({
        ...d,
        account: d?.accounts?.name || '',
        stage: d?.pipeline_stages?.name || '',
        owner: d?.owner?.full_name || '',
        closeDate: d?.close_date || '',
        createdDate: d?.created_at?.split('T')?.[0] || ''
      }));

      setDeals(mapped);
      setTotalCount(count || 0);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id, currentPage, itemsPerPage, filters, sortConfig]);

  useEffect(() => { fetchStages(); }, [fetchStages]);
  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleMenuToggle = () => setIsSidebarOpen(!isSidebarOpen);
  const handleSidebarClose = () => setIsSidebarOpen(false);

  const handleDealClick = (deal) => {
    setSelectedDeal(deal);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedDeal(null);
  };

  const handleSelectDeal = (dealId, isSelected) => {
    if (isSelected) setSelectedDeals(prev => [...prev, dealId]);
    else setSelectedDeals(prev => prev?.filter(id => id !== dealId));
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) setSelectedDeals(deals?.map(d => d?.id));
    else setSelectedDeals([]);
  };

  const handleAddDeal = async () => {
    if (!newDeal?.name?.trim()) return;
    setSaving(true);
    try {
      const { error: insertError } = await supabase?.from('deals')?.insert({ ...newDeal, org_id: profile?.org_id, owner_id: profile?.id, value: Number(newDeal?.value) || 0 });
      if (insertError) throw insertError;

      // Auto-log activity
      await supabase?.from('activities')?.insert({
        org_id: profile?.org_id,
        owner_id: profile?.id,
        type: 'note',
        subject: `Deal Created: ${newDeal?.name}`,
        description: `New deal added${newDeal?.value ? ` — $${Number(newDeal?.value)?.toLocaleString()}` : ''}`,
        due_date: new Date()?.toISOString(),
        priority: newDeal?.priority || 'medium',
        status: 'pending'
      });

      setNewDeal({ name: '', value: '', stage_id: '', close_date: '', probability: 50, priority: 'medium' });
      setIsAddModalOpen(false);
      fetchDeals();
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm('Move this deal to trash?')) return;
    try {
      await supabase?.from('deals')?.update({ deleted_at: new Date()?.toISOString() })?.eq('id', dealId);
      toast?.success('Moved to Trash');
      fetchDeals();
      if (selectedDeal?.id === dealId) handleDrawerClose();
    } catch (err) {
      setError(err?.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Deals - Wilcart CRM</title>
        <meta name="description" content="Manage your deals in Wilcart CRM." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={handleMenuToggle} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Deals</h1>
                <p className="text-muted-foreground mt-1">Track and manage your sales opportunities</p>
              </div>
              <div className="flex items-center space-x-3 mt-4 sm:mt-0">
                {selectedDeals?.length > 0 && (
                  <span className="text-sm text-muted-foreground">{selectedDeals?.length} selected</span>
                )}
                <Button onClick={() => setIsAddModalOpen(true)}>
                  <Icon name="Plus" size={16} className="mr-2" />
                  Add Deal
                </Button>
              </div>
            </div>

            <DealsFilters
              filters={filters}
              onFiltersChange={setFilters}
              stages={stages}
              onClearFilters={handleClearFilters}
              dealCount={totalCount}
              onBulkAction={handleBulkAction}
              selectedCount={selectedDeals?.length}
            />

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
            )}

            {loading ? (
              <div className="space-y-3">
                {[1,2,3,4,5]?.map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}
              </div>
            ) : (
              <DealsTable
                deals={deals}
                selectedDeals={selectedDeals}
                onSelectDeal={handleSelectDeal}
                onSelectAll={handleSelectAll}
                onDealClick={handleDealClick}
                sortConfig={sortConfig}
                onSort={(key) => setSortConfig(prev => ({ key, direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc' }))}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
              />
            )}

            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={() => {}}
            />
          </div>
        </main>

        {/* Add Deal Modal */}
        {isAddModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">Add Deal</h2>
                <button onClick={() => setIsAddModalOpen(false)} className="text-muted-foreground hover:text-foreground">
                  <Icon name="X" size={20} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Deal Name *</label>
                  <input type="text" value={newDeal?.name} onChange={e => setNewDeal(p => ({...p, name: e?.target?.value}))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Value ($)</label>
                  <input type="number" value={newDeal?.value} onChange={e => setNewDeal(p => ({...p, value: e?.target?.value}))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Stage</label>
                  <select value={newDeal?.stage_id} onChange={e => setNewDeal(p => ({...p, stage_id: e?.target?.value}))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                    <option value="">Select stage</option>
                    {stages?.map(s => <option key={s?.id} value={s?.id}>{s?.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Close Date</label>
                  <input type="date" value={newDeal?.close_date} onChange={e => setNewDeal(p => ({...p, close_date: e?.target?.value}))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Priority</label>
                  <select value={newDeal?.priority} onChange={e => setNewDeal(p => ({...p, priority: e?.target?.value}))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
                <Button className="flex-1" onClick={handleAddDeal} loading={saving} disabled={saving || !newDeal?.name?.trim()}>
                  {saving ? 'Saving...' : 'Add Deal'}
                </Button>
              </div>
            </div>
          </div>
        )}

        <DealDrawer
          deal={selectedDeal}
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
        />
      </div>
    </>
  );
};

export default DealsPage;